import React, { useState, useEffect } from 'react';
import { Button, toast } from '@ncoder/ui';
import { Edit, Wifi, WifiOff, MonitorPlay, Loader2, Check, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import { enableLwcPreview } from '@/services/multi-agent';
import env from '@/config/env';
import { getAuthToken } from '@/dataProvider/axios';
import { safeHttpsUrl } from '@/lib/safeUrl';

/** Per-org "Live Preview" enable control. Live Preview (Local Dev) is an org
 *  setting `sf lightning dev` needs; this deploys it deliberately (admin action).
 *  The org doesn't return the pref on retrieve, so we remember the enabled state
 *  per-org in localStorage — keyed by orgId — so it survives reload / re-login
 *  instead of resetting to the Enable affordance every time. */
const LivePreviewControl: React.FC<{ orgId: string }> = ({ orgId }) => {
  const storeKey = `ncoder:lwc-preview-enabled:${orgId}`;
  const [state, setState] = useState<'idle' | 'enabling' | 'enabled'>(() => {
    try { return localStorage.getItem(storeKey) ? 'enabled' : 'idle'; } catch { return 'idle'; }
  });

  const enable = async () => {
    setState('enabling');
    try {
      const r = await enableLwcPreview(orgId);
      if (r.enabled) {
        setState('enabled');
        try { localStorage.setItem(storeKey, '1'); } catch { /* storage unavailable */ }
        toast({ title: 'Live Preview enabled', description: 'This org can now render generated components.' });
      } else {
        setState('idle');
        toast({ title: "Couldn't enable Live Preview", description: r.detail || 'Deploy failed — admin permission may be required.' });
      }
    } catch (e) {
      setState('idle');
      toast({ title: "Couldn't enable Live Preview", description: e instanceof Error ? e.message : 'Request failed' });
    }
  };

  if (state === 'enabled') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-600">
        <Check className="h-3 w-3" /> Live Preview
      </span>
    );
  }
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 shrink-0 gap-1.5 px-2 text-[11px] text-muted-foreground hover:text-foreground"
      onClick={enable}
      disabled={state === 'enabling'}
      title="Enable Live Preview (Local Dev) for this org — required to render generated LWCs/Flows against it. Needs admin permission."
    >
      {state === 'enabling'
        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
        : <MonitorPlay className="h-3.5 w-3.5" />}
      {state === 'enabling' ? 'Enabling…' : 'Enable Live Preview'}
    </Button>
  );
};

/** Connect the logged-in user's Slack account to ncoder (weave). One click runs the Slack
 *  OAuth; the callback stores the user's Slack id on their ncoder user (users.slack_user_id),
 *  which is how weave identifies who they are in Slack. Shows a green "Slack Connected" badge
 *  once bound (checked on mount + reflected from the ?slack=connected redirect). */
const SlackConnectControl: React.FC = () => {
  const [state, setState] = useState<'idle' | 'connecting' | 'connected'>('idle');

  useEffect(() => {
    // reflect the post-OAuth redirect (?slack=connected) immediately
    if (new URLSearchParams(window.location.search).get('slack') === 'connected') {
      setState('connected');
    }
    // and check the persisted state (has this user's Slack been bound?)
    (async () => {
      try {
        const res = await fetch(`${env.API_URL}/slack/connection-status`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        const data = await res.json().catch(() => ({}));
        if (data.connected) setState((s) => (s === 'connecting' ? s : 'connected'));
      } catch { /* status is best-effort */ }
    })();
  }, []);

  const connect = async () => {
    setState('connecting');
    try {
      const res = await fetch(`${env.API_URL}/slack/connect-url`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      // https only: a `javascript:` value here would run as the app (security report F2).
      const authorizeUrl = res.ok ? safeHttpsUrl(data.url) : null;
      if (authorizeUrl) {
        window.location.assign(authorizeUrl); // → Slack authorize, then callback binds the id
        return;
      }
      setState('idle');
      toast({
        title: "Couldn't start Slack connect",
        description: data.error || (res.ok ? 'The server did not return a valid Slack link.' : `HTTP ${res.status}`),
      });
    } catch (e) {
      setState('idle');
      toast({ title: "Couldn't start Slack connect", description: e instanceof Error ? e.message : 'Request failed' });
    }
  };

  if (state === 'connected') {
    return (
      <span
        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-600"
        title="Your Slack account is connected to ncoder (weave)."
      >
        <Check className="h-3 w-3" /> Slack Connected
      </span>
    );
  }
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 shrink-0 gap-1.5 px-2 text-[11px] text-muted-foreground hover:text-foreground"
      onClick={connect}
      disabled={state === 'connecting'}
      title="Connect your Slack account to ncoder (weave) — so weave knows who you are in Slack."
    >
      {state === 'connecting'
        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
        : <MessageSquare className="h-3.5 w-3.5" />}
      {state === 'connecting' ? 'Connecting…' : 'Slack Integration'}
    </Button>
  );
};

export interface Connection {
  id: number;
  name: string;
  url: string;
  status: 'connected' | 'disconnected';
  lastSync: string;
  version: string;
  objects?: number;
  users?: number;
  storage?: string;
  apexClasses?: number;
  apexTriggers?: number;
  lightningComponents?: number;
  orgId?: string;
}

interface ConnectionCardProps {
  connection: Connection;
  onSync?: (id: number) => void;
  onEdit?: (orgId: string, currentName: string) => void;
}

export const ConnectionCard: React.FC<ConnectionCardProps> = ({ connection, onEdit }) => {
  const isConnected = connection.status === 'connected';

  return (
    <div className="group flex items-center gap-4 rounded-lg border border-border/60 bg-card/40 px-4 py-3 transition-colors hover:bg-card/80">
      {/* Status indicator */}
      <div className={cn(
        "flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
        isConnected ? "bg-emerald-500/10" : "bg-muted/60"
      )}>
        {isConnected
          ? <Wifi className="h-3.5 w-3.5 text-emerald-500" />
          : <WifiOff className="h-3.5 w-3.5 text-muted-foreground" />
        }
      </div>

      {/* Org identity */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-foreground">{connection.name}</span>
          {connection.version && connection.version !== 'N/A' && (
            <span className="shrink-0 rounded border border-border px-1.5 py-px font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
              {connection.version}
            </span>
          )}
        </div>
        <span className="truncate text-xs text-muted-foreground">{connection.url}</span>
      </div>

      {/* Stats — labels live in the column header row above */}
      <div className="hidden shrink-0 items-center gap-6 md:flex">
        {[connection.objects, connection.apexClasses, connection.lightningComponents].map((value, i) => (
          <div key={i} className="w-10 text-center text-sm font-semibold tabular-nums text-foreground">
            {(value ?? 0).toLocaleString()}
          </div>
        ))}
      </div>

      {/* Actions — fixed width (matches the ConnectionManager header spacer) so the stats
          stay aligned under their column headers; justify-end right-aligns the controls. */}
      <div className="flex w-80 shrink-0 items-center justify-end gap-2">
        {connection.orgId && <LivePreviewControl orgId={connection.orgId} />}
        <SlackConnectControl />
        {onEdit && connection.orgId && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
            onClick={() => onEdit(connection.orgId!, connection.name)}
          >
            <Edit className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};
